import { FC, useContext, useEffect, useState } from "react";

import SpecialtyGroupExceptionsService from "@app/projects/specialtyGroupExceptionsService";
import { formatDateTime, LoadingSpinner } from "@app/shared";
import { QESAlert, QESButton } from "@app/shared/qes-standards";
import { ColumnConfig, Waffles } from "../../shared/waffles";

import {
  SpecialtyGroupExceptionDisplay,
} from "@app/projects/results/common";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { capitalizeFirstLetter, truncateStringWithEllipsis } from "@app/shared/utility";


interface UnprocessedSpecialtyGroupExceptionsProps {
  projectId: number;
}

interface PendingWaffleToppings {
  key: string;
  actionBy: string;
  actionDate: string;
  comment: string;
  county: string;
  specialtyGroup: string;
  status: string;
}

const PendingUnprocessedSpecialtyGroupExceptionsChanges: FC<React.PropsWithChildren<UnprocessedSpecialtyGroupExceptionsProps>> = (props: UnprocessedSpecialtyGroupExceptionsProps) => {
  const specialtyGroupExceptionsService = new SpecialtyGroupExceptionsService();
  const [unprocessedSpecialtyGroupExceptions, setUnprocessedSpecialtyGroupExceptions] = useState<SpecialtyGroupExceptionDisplay[]>();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const flyoutContext = useContext(FlyoutContext);

  useEffect(() => {
    setLoading(true);

    specialtyGroupExceptionsService.getUnprocessedSpecialtyGroupExceptions(props.projectId)
      .then((result) => {
        setUnprocessedSpecialtyGroupExceptions(result);
      }, () => {
        setError(true);
      });

    setLoading(false);
  }, []);

  const renderLoadedContent = () => {
    if (unprocessedSpecialtyGroupExceptions.length > 0) {
      return (
        <>
          {unprocessedSpecialtyGroupExceptions.length > 0 && renderUnprocessedSpecialtyGroupExceptions(unprocessedSpecialtyGroupExceptions)}
          <FlyoutFooter
            submitButton={<QESButton qesType="primary" qesTestId="pending-providers__process" onClick={() => submit()}>Process Specialty Group Exceptions</QESButton>}
          />
        </>
      );
    } else {
      return renderNoResults();
    }
  };

  const submit = () => {
    specialtyGroupExceptionsService.processUnprocessedSpecialtyGroupExceptions(props.projectId);
    flyoutContext.handleCloseFlyout();
  }

  return (
    <>
      {error
        ? <QESAlert type="danger">Something went wrong on our end.</QESAlert>
        : !!unprocessedSpecialtyGroupExceptions && !loading ?
          renderLoadedContent() : <LoadingSpinner />}
    </>
  );
};

const columnConfig: ColumnConfig = {
  actionBy: {
    displayText: "Action By",
    noSort: true,
    testId: "action-by",
  },
  actionDate: {
    displayText: "Action Date",
    noSort: true,
    testId: "action-date",
  },
  comment: {
    displayText: "Comment",
    noSort: true,
    testId: "comment",
  },
  county: {
    displayText: "County",
    noSort: true,
    testId: "county",
  },
  exceptionNarrative: {
    displayText: "Exception Narrative",
    noSort: true,
    testId: "exception-narrative",
  },
  specialtyGroup: {
    displayText: "Specialty Group",
    noSort: true,
    testId: "specialty-group",
  },
  status: {
    displayText: "Status",
    noSort: true,
    testId: "status",
  },
};

const renderUnprocessedSpecialtyGroupExceptions = (unprocessedSpecialtyGroupExceptions: SpecialtyGroupExceptionDisplay[]) => {

  const commentAndNarrativeLength = 25;

  /* eslint-disable sort-keys */
  const data: PendingWaffleToppings[] = unprocessedSpecialtyGroupExceptions.map((usge) => (
    {
      key: usge.specialtyGroupExceptionId.toString(),
      actionBy: usge.updatedByUserFullName,
      actionDate: formatDateTime(usge.dateUpdated),
      comment: truncateStringWithEllipsis(usge.comment, commentAndNarrativeLength),
      exceptionNarrative: truncateStringWithEllipsis(usge.exceptionNarrative, commentAndNarrativeLength),
      county: `${usge.regionSetDescription}, ${usge.regionSetStateName}`,
      specialtyGroup: usge.specialtyGroupCode,
      status: capitalizeFirstLetter(usge.specialtyGroupExceptionStatus),
    }
  ));
  /* eslint-enable sort-keys */

  return (
    <Waffles<PendingWaffleToppings>
      columns={columnConfig}
      data={data}
      testId="pending-exceptions"
    />
  );
};

const renderNoResults = () => {
  return (<QESAlert type="danger">No unprocessed specialty group exceptions found.</QESAlert>);
};

export { PendingUnprocessedSpecialtyGroupExceptionsChanges };
